<script setup lang="ts">
import BackButton from '@/components/BackButton.vue'
import { RouteLocationRaw } from 'vue-router'
import { computed, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    title: string
    backButton?: boolean
    text?: string
    image?: string
    color?: boolean
    type?: 'personal' | 'corporative'
    backButtonTo?: RouteLocationRaw
    fallbackImage?: string
    hasMeta?: boolean
  }>(),
  {
    backButton: false,
    text: '',
    image: '',
    color: false,
    type: 'personal',
    fallbackImage: '',
    backButtonTo: '',
    hasMeta: false,
  }
)

const BASE = 'https://dev.eve.com.vc'

const finalSrc = computed<string | null>(() => {
  const raw = (props.image ?? '').trim()
  if (!raw) return null

  if (/^(data:|blob:)/i.test(raw)) return raw

  if (/^https?:\/\//i.test(raw)) {
    try {
      const u = new URL(raw)
      u.pathname = u.pathname.replace(/^\/uploads\//, '/resources/')
      return u.toString()
    } catch {
      return raw
    }
  }

  if (/^\/?uploads\//i.test(raw)) {
    return `${BASE}/${raw.replace(/^\/?uploads\//i, 'resources/')}`
  }

  return raw
})

const showImg = ref(true)

watch(
  finalSrc,
  (val) => {
    showImg.value = Boolean(val)
  },
  { immediate: true }
)

function onImgError() {
  showImg.value = false
}

const hasRealImage = computed(() => !!finalSrc.value && showImg.value)
const hasFallback = computed(() => !!(props.fallbackImage || '').trim())

const showAvatar = computed(() => {
  if (props.type === 'corporative') {
    return hasRealImage.value
  }

  return hasRealImage.value || hasFallback.value
})

const avatarRadiusStyle = computed(() =>
  props.type === 'personal' ? 'border-radius: 9999px' : 'border-radius: 8px'
)
const hasMetaSlot = computed(() => props.hasMeta)

const showSubtitle = computed(() => {
  if (!props.text) return false
  return props.type !== 'corporative'
})
</script>

<template>
  <div class="page-header-root" :class="{ 'no-avatar': !showAvatar }">
    <div v-if="props.backButton" class="back-col">
      <BackButton :to="props.backButtonTo" />
    </div>

    <div v-if="showAvatar" class="avatar-col">
      <v-img
        v-if="hasRealImage"
        :key="finalSrc ?? 'no-image'"
        :src="finalSrc || ''"
        alt="contact-image"
        width="48"
        height="48"
        cover
        eager
        :style="avatarRadiusStyle"
        @error="onImgError"
      >
        <template #placeholder>
          <div class="avatar-placeholder">
            <v-progress-circular indeterminate size="18" />
          </div>
        </template>
      </v-img>

      <div
        v-else
        class="avatar-fallback"
        :class="{ 'avatar-fallback--corp': props.type === 'corporative' }"
        :style="avatarRadiusStyle"
      >
        <img
          v-if="props.fallbackImage"
          :src="props.fallbackImage"
          alt="fallback"
          class="avatar-fallback-img"
        />
        <v-icon
          v-else
          icon="mdi-domain"
          size="22"
          class="avatar-fallback-icon"
        />
      </div>
    </div>

    <div class="title-col">
      <h2 class="title-text">
        {{ props.title }}
      </h2>

      <div v-if="hasMetaSlot" class="meta-slot">
        <slot name="meta" />
      </div>

      <p v-if="showSubtitle" class="subtitle-text">
        {{ props.text }}
      </p>
    </div>
  </div>
</template>

<style scoped>
.page-header-root {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
}

.back-col,
.avatar-col {
  flex: 0 0 auto;
}

.title-col {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
}

.title-text {
  color: var(--opacity-text--primary, rgba(0, 0, 0, 0.87));
  font-family: var(--Typography-fontFamilies-Open-Sans, 'Open Sans');
  font-size: var(--fontSize-35, 20px);
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.05px;

  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.meta-slot {
  display: flex;
  align-items: center;
  min-height: 20px;
}

.subtitle-text {
  width: 100%;
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--v-theme-grey-darken-1, #757575);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.avatar-placeholder {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
}

.avatar-fallback {
  width: 48px;
  height: 48px;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.avatar-fallback--corp {
  width: 32px;
  height: 32px;
  background: #8dc63f;
}

.avatar-fallback-img {
  width: 24px;
  height: 24px;
  object-fit: contain;
  opacity: 0.95;
}

.avatar-fallback-icon {
  color: rgba(0, 0, 0, 0.54);
}

.avatar-fallback--corp .avatar-fallback-icon {
  color: #ffffff;
}

@media (max-width: 959.98px) {
  .page-header-root.no-avatar {
    gap: 0;
  }

  .page-header-root.no-avatar .title-col {
    margin-left: 0;
  }
}
</style>
